import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Plus, ImageIcon } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';
import { COLLECTIONS, MY_COLLECTIONS } from '../data/mock';

const CollectionsPage = () => {
  return (
    <Layout>
      
      {/* Hero Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-[hsl(var(--cream))] via-white to-[hsl(var(--mint))]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-2 bg-[hsl(350,70%,65%)]/10 rounded-full text-sm font-medium text-[hsl(350,70%,55%)] mb-4">
              Our Collections
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Choose Your
              <span className="gradient-text"> Perfect Style</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              From adorable pet portraits to romantic couple artwork, we have the perfect 
              collection for every occasion. Each piece is uniquely crafted using AI technology.
            </p>
          </div>
        </div>
      </section>

      {/* Main Collections Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Featured Collections</h2>
            <p className="text-gray-600 mt-1">Our most popular custom print categories</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {COLLECTIONS.map((collection, index) => (
              <Link 
                key={collection.id} 
                to={`/collections/${collection.id}`}
                className="group"
              >
                <Card className="border-0 shadow-soft overflow-hidden hover:shadow-soft-lg transition-all duration-500 hover-lift h-full">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={collection.image}
                      alt={collection.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                    
                    {/* Badge */}
                    <div className="absolute top-4 left-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold text-white ${
                        index === 0 ? 'bg-[hsl(350,70%,55%)]' :
                        index === 1 ? 'bg-[hsl(20,90%,60%)]' :
                        index === 2 ? 'bg-[hsl(var(--mint-dark))]' :
                        'bg-violet-500'
                      }`}>
                        {collection.products.length} Products
                      </span>
                    </div>
                    
                    {/* Price */}
                    <div className="absolute top-4 right-4">
                      <span className="inline-block px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-700">
                        {collection.priceRange}
                      </span>
                    </div>
                    
                    {/* Title overlay */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-2xl font-bold text-white mb-1">{collection.title}</h3>
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    <p className="text-gray-600 mb-6 leading-relaxed">{collection.shortDescription}</p>
                    
                    {/* Product Tags */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {collection.products.map((product) => (
                        <span 
                          key={product} 
                          className="px-3 py-1.5 bg-[hsl(var(--cream))] rounded-lg text-sm font-medium text-gray-600"
                        >
                          {product}
                        </span>
                      ))}
                    </div>
                    
                    {/* CTA */}
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[hsl(350,70%,55%)] font-semibold group-hover:translate-x-1 transition-transform duration-300 flex items-center gap-2 text-sm">
                        Customize This
                        <ArrowRight className="w-4 h-4" />
                      </span>
                      <Link
                        to={`/shop?collection=${collection.id}`}
                        onClick={e => e.stopPropagation()}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow hover:shadow-md transition-all hover:scale-105"
                        style={{ background: 'linear-gradient(135deg,hsl(350,70%,65%),hsl(20,90%,70%))' }}
                      >
                        Shop Products
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* My Collections Section - Placeholder for user's own collections */}
      <section className="py-16 bg-gradient-to-b from-[hsl(var(--cream))] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">My Collections</h2>
            <p className="text-gray-600 mt-1">Your personal showcase of custom designs</p>
          </div>
          
          {MY_COLLECTIONS.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {MY_COLLECTIONS.map((collection) => (
                <Card key={collection.id} className="border-0 shadow-soft overflow-hidden hover:shadow-soft-lg transition-all duration-300 hover-lift">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={collection.image}
                      alt={collection.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900">{collection.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{collection.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            /* Empty State */
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Placeholder cards for future collections */}
              {[1, 2, 3, 4].map((item) => (
                <Card key={item} className="border-2 border-dashed border-gray-200 bg-gray-50/50 hover:border-[hsl(350,70%,65%)]/30 hover:bg-[hsl(var(--cream))]/50 transition-all duration-300">
                  <CardContent className="p-6 flex flex-col items-center justify-center min-h-[240px] text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                      {item === 1 ? (
                        <Plus className="w-8 h-8 text-gray-400" />
                      ) : (
                        <ImageIcon className="w-8 h-8 text-gray-300" />
                      )}
                    </div>
                    {item === 1 ? (
                      <>
                        <h3 className="font-semibold text-gray-700 mb-1">Coming Soon</h3>
                        <p className="text-sm text-gray-500">New designs will be added here</p>
                      </>
                    ) : (
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-24 mx-auto" />
                        <div className="h-3 bg-gray-100 rounded w-16 mx-auto" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          {/* Info text */}
          <div className="mt-8 text-center">
            <p className="text-gray-500 text-sm">
              Stay tuned! We're constantly adding new designs and collections. 
              <Link to="/contact" className="text-[hsl(350,70%,55%)] hover:underline ml-1">
                Contact us
              </Link> for custom requests.
            </p>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            Can't Find What You're Looking For?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            We love custom requests! If you have a specific idea in mind, reach out to us 
            and we'll work with you to create something truly unique.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-gradient-to-r from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)] hover:from-[hsl(350,70%,60%)] hover:to-[hsl(20,90%,65%)] text-white shadow-soft-lg">
              Contact Us
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default CollectionsPage;
